import { attach, createEffect, createEvent, createStore, sample } from 'effector';
import { generateUuid } from '@lib/uuid';
import { clientMoveNote } from '@nx-frontend/shared/api/webnotes/client';
import { StartDuplicateFolderTaskPayload, TaskResponseDto } from '@nx-frontend/shared/types';

export enum DuplicateFolderStatus {
  NONE = 'none',
  PROGRESS = 'progress',
  COMPLETED = 'completed',
}

export const ITEMS_TO_SHOW_DUPLICATION_PROGRESS = 0; // (items inside + original folder)
export const duplicateFolderStateLocalStorageKey = 'duplicateFolderStateLocalStorage';

export interface DuplicateFolderState {
  status: DuplicateFolderStatus;
  taskId: string;
  jobId: string;
  total: number;
  current: number;
  folderName: string;
  workspaceId: string;
  globalId: string;
  parentId: string;
  copyGlobalId: string;
  TID: NodeJS.Timeout | number | null;
}

export const initialDuplicateFolderState: DuplicateFolderState = {
  status: DuplicateFolderStatus.NONE,
  taskId: '',
  jobId: '',
  total: 0,
  current: 0,
  folderName: '',
  workspaceId: '',
  globalId: '',
  parentId: '',
  copyGlobalId: '',
  TID: null,
};

export interface StartDuplicateFolderTaskFxPayload extends StartDuplicateFolderTaskPayload {
  taskId: string;
}

export type UpdateDuplicateFolderStatePayload = Partial<DuplicateFolderState>;

export const $duplicateFolder = createStore(initialDuplicateFolderState);

export const startDuplicateFolderTaskEvent = createEvent<StartDuplicateFolderTaskPayload>();

export const updateDuplicateFolderStateEvent = createEvent<UpdateDuplicateFolderStatePayload>();

export const resetDuplicateFolderStateEvent = createEvent();

export const duplicateFolderTaskFx = createEffect(async (payload: StartDuplicateFolderTaskFxPayload) => {
  const { workspaceId, globalId, parentId, taskId, callback } = payload;

  const data: TaskResponseDto | null = await clientMoveNote({
    workspaceFrom: workspaceId,
    workspaceId: workspaceId,
    noteFrom: globalId,
    parentId,
    remove: false,
    titleSuffix: '(Copy)',
    duplicating: false,
    taskId,
    cnt: true,
    notIncludePortalShare: true,
  });

  if (callback) {
    callback(data);
  }

  return data;
});

$duplicateFolder.on(startDuplicateFolderTaskEvent, (state, payload) => {
  return {
    ...state,
    folderName: payload.folderName,
    workspaceId: payload.workspaceId,
    globalId: payload.globalId,
    parentId: payload.parentId,
    taskId: generateUuid(),
  };
});

sample({
  source: $duplicateFolder,
  clock: startDuplicateFolderTaskEvent,
  fn: (_, payload) => {
    return { ...payload, taskId: _.taskId };
  },
  target: duplicateFolderTaskFx,
});

function saveStateToLocalStorage(state: DuplicateFolderState) {
  localStorage.setItem(duplicateFolderStateLocalStorageKey, JSON.stringify(state));
}

$duplicateFolder.on(duplicateFolderTaskFx.doneData, (state, data) => {
  const cnt = data?.cnt ?? 0;
  const newState = {
    ...state,
    total: cnt,
    current: 0,
    status: cnt > ITEMS_TO_SHOW_DUPLICATION_PROGRESS ? DuplicateFolderStatus.PROGRESS : DuplicateFolderStatus.NONE,
    jobId: data?.id ?? '',
  };
  saveStateToLocalStorage(newState);
  return newState;
});

export const getDuplicateFolderTaskIdFx = attach({
  source: $duplicateFolder,
  effect: (state: DuplicateFolderState) => {
    return state.taskId;
  },
});

export const getDuplicateFolderJobIdFx = attach({
  source: $duplicateFolder,
  effect: (state: DuplicateFolderState) => {
    return state.jobId;
  },
});

export const getDuplicateFolderStateFx = attach({
  source: $duplicateFolder,
  effect: (state: DuplicateFolderState) => {
    return state;
  },
});

$duplicateFolder.on(updateDuplicateFolderStateEvent, (state, payload) => {
  const newState = {
    ...state,
    ...payload,
  };
  saveStateToLocalStorage(newState);
  return newState;
});

$duplicateFolder.on(resetDuplicateFolderStateEvent, () => {
  saveStateToLocalStorage(initialDuplicateFolderState);
  return initialDuplicateFolderState;
});

export const loadDuplicateFolderStateFx = createEffect(() => {
  const state = localStorage.getItem(duplicateFolderStateLocalStorageKey);
  return state ? JSON.parse(state) : initialDuplicateFolderState;
});

$duplicateFolder.on(loadDuplicateFolderStateFx.doneData, (_, state) => state);
